import axios from 'axios';
import apiClient from './apiClient';
import {
  Child,
  ChildProgress,
  ChildEnrollment,
  ParentStudentRelationship,
  LinkStudentByEmailData,
  GetChildrenResponse,
  GetChildProgressResponse,
  GetChildEnrollmentsResponse,
  AddChildResponse,
  RemoveChildResponse,
  GetRelationshipsResponse
} from '../types/parent';

/**
 * Get all children for the authenticated parent
 * @returns Array of children (User objects)
 */
export const getChildren = async (): Promise<Child[]> => {
  try {
    const response = await apiClient.get<GetChildrenResponse>('/api/parent/children');
    return response.data.children;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch children';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get progress metrics for a specific child
 * @param childId - Child user ID
 * @returns ChildProgress object with metrics
 */
export const getChildProgress = async (childId: number): Promise<ChildProgress> => {
  try {
    const response = await apiClient.get<GetChildProgressResponse>(
      `/api/parent/children/${childId}/progress`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch child progress';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get enrollments for a specific child
 * @param childId - Child user ID
 * @returns Array of enrollment objects
 */
export const getChildEnrollments = async (childId: number): Promise<ChildEnrollment[]> => {
  try {
    const response = await apiClient.get<GetChildEnrollmentsResponse>(
      `/api/parent/children/${childId}/enrollments`
    );
    return response.data.enrollments;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch child enrollments';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Link an existing student to the authenticated parent
 * @param linkData - Student email and relationship details
 * @returns Created relationship
 */
export const addChild = async (
  linkData: LinkStudentByEmailData
): Promise<ParentStudentRelationship> => {
  try {
    const response = await apiClient.post<AddChildResponse>(
      '/api/parent/children',
      linkData
    );
    return response.data.relationship;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to add child';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Remove a child from the authenticated parent
 * @param childId - Child user ID to remove
 */
export const removeChild = async (childId: number): Promise<void> => {
  try {
    await apiClient.delete<RemoveChildResponse>(`/api/parent/children/${childId}`);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to remove child';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Get all parent-child relationships for the authenticated parent
 * @returns Array of relationships with details
 */
export const getRelationships = async (): Promise<ParentStudentRelationship[]> => {
  try {
    const response = await apiClient.get<GetRelationshipsResponse>(
      '/api/parent/relationships'
    );
    return response.data.relationships;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Failed to fetch relationships';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};
